import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import httpClient, { extractApiError } from "../api/httpClient";
import { dataGridSx, PageHeader, PagePanel, PageShell } from "../components/PageLayout";
import { useUI } from "../contexts/UIContext";

const defaultForm = {
  deviceId: "",
  platform: "WEB",
  pushToken: "",
  appVersion: "1.0.0",
  locale: "it-IT",
};

export default function Dispositivi() {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const { showNotification } = useUI();

  const loadDevices = useCallback(async () => {
    setLoading(true);
    try {
      const response = await httpClient.get("/alerts/notifications/my-devices");
      setRows(response?.data || []);
    } catch (error) {
      showNotification(extractApiError(error), "error");
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => {
    loadDevices();
  }, [loadDevices]);

  const updateField = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const registerDevice = async () => {
    try {
      if (!form.deviceId || !form.platform || !form.pushToken) {
        showNotification("Compila deviceId, platform e pushToken", "warning");
        return;
      }

      await httpClient.post("/alerts/notifications/register-device", {
        ...form,
        deviceId: form.deviceId.trim(),
        pushToken: form.pushToken.trim(),
      });

      showNotification("Dispositivo registrato", "success");
      setForm(defaultForm);
      await loadDevices();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const unregisterDevice = async (deviceId) => {
    try {
      await httpClient.delete(`/alerts/notifications/unregister-device/${deviceId}`);
      showNotification("Dispositivo disattivato", "success");
      await loadDevices();
    } catch (error) {
      showNotification(extractApiError(error), "error");
    }
  };

  const columns = useMemo(
    () => [
      { field: "deviceId", headerName: "Device ID", minWidth: 220, flex: 1.2 },
      { field: "platform", headerName: "Platform", minWidth: 120, flex: 0.6 },
      { field: "appVersion", headerName: "App Version", minWidth: 140, flex: 0.6 },
      { field: "locale", headerName: "Locale", minWidth: 120, flex: 0.5 },
      {
        field: "enabled",
        headerName: "Attivo",
        minWidth: 110,
        valueGetter: (params) => (params?.row?.enabled ? "SI" : "NO"),
      },
      {
        field: "actions",
        type: "actions",
        width: 80,
        getActions: (params) => [
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Unregister"
            onClick={() => unregisterDevice(params.row.deviceId)}
          />,
        ],
      },
    ],
    [unregisterDevice]
  );

  return (
    <PageShell>
      <PageHeader
        title="Dispositivi notifiche"
        subtitle="Registrazione e gestione endpoint push con la stessa grammatica visuale introdotta nella pagina turni."
      />

      <PagePanel tone="cool" sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField label="Device ID" value={form.deviceId} onChange={updateField("deviceId")} fullWidth />
          <TextField select label="Platform" value={form.platform} onChange={updateField("platform")} sx={{ minWidth: 150 }}>
            <MenuItem value="WEB">WEB</MenuItem>
            <MenuItem value="ANDROID">ANDROID</MenuItem>
            <MenuItem value="IOS">IOS</MenuItem>
          </TextField>
          <TextField label="Push Token" value={form.pushToken} onChange={updateField("pushToken")} fullWidth />
          <Button variant="contained" startIcon={<AddIcon />} onClick={registerDevice}>
            Registra
          </Button>
        </Stack>
      </PagePanel>

      <PagePanel tone="neutral" sx={{ p: 1.5 }}>
        {loading ? (
          <Skeleton variant="rounded" height={380} />
        ) : (
          <DataGrid rows={rows} getRowId={(row) => row.deviceId} columns={columns} autoHeight disableRowSelectionOnClick pageSizeOptions={[10, 25]} sx={dataGridSx} />
        )}
      </PagePanel>
    </PageShell>
  );
}
